import * as Sentry from "@sentry/react"

import { getMonitorPlatform } from "./monitor-context"
import { serializeError } from "./error-serializer"
import { isIgnoredErrorMessage } from "./sentry-filters"
import { scrubUrl, scrubValue } from "./sentry-privacy"
import { markSentryInitialized } from "./sentry-state"
import type { SentryConfig } from "./types"

function isAuthTrace(name: string | undefined, op: string | undefined) {
  const haystack = `${name ?? ""} ${op ?? ""}`.toLowerCase()
  return haystack.includes("auth")
}

export async function initSentryClient(config: SentryConfig) {
  if (!config.dsn) {
    console.warn(
      "[logging] VITE_SENTRY_DSN is not set. Journey metrics will log to console only."
    )
    return
  }

  const integrations = [
    Sentry.breadcrumbsIntegration({
      console: false,
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
    Sentry.browserTracingIntegration(),
  ]

  if (config.replay?.enabled) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      })
    )
  }

  const defaultTracesSampleRate = config.tracesSampleRate ?? 0

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release ? `be-miniapp@${config.release}` : undefined,
    sendDefaultPii: false,
    tracesSampleRate: defaultTracesSampleRate,
    tracesSampler: (samplingContext) => {
      if (
        isAuthTrace(
          samplingContext.name,
          samplingContext.attributes?.["sentry.op"] as string | undefined
        )
      ) {
        return 1
      }

      return defaultTracesSampleRate
    },
    sampleRate: config.sampleRate ?? 1,
    replaysSessionSampleRate: config.replay?.replaysSessionSampleRate ?? 0,
    replaysOnErrorSampleRate: config.replay?.replaysOnErrorSampleRate ?? 0,
    integrations,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      /^Network Error$/,
      /^Load failed$/,
      /^AbortError$/,
      /^ChunkLoadError$/,
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "http" && breadcrumb.data) {
        breadcrumb.data.url = scrubUrl(String(breadcrumb.data.url ?? ""))
      }

      if (breadcrumb.data) {
        breadcrumb.data = scrubValue(breadcrumb.data) as Record<string, unknown>
      }

      return breadcrumb
    },
    beforeSend(event) {
      if (isIgnoredErrorMessage(event.message)) return null

      const serialized = serializeError(
        event.extra?.__serialized__ ?? event.extra?.error
      )

      if (
        event.exception?.values?.[0]?.type === "UnhandledRejection" &&
        serialized.message !== "Unknown error"
      ) {
        const title = serialized.errorCode
          ? `[${serialized.api ?? "promise"}] ${serialized.errorCode}: ${serialized.message}`
          : serialized.message

        event.message = title
        event.exception.values[0].value = title
      }

      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url)
      }

      if (event.tags) {
        event.tags = scrubValue(event.tags) as Record<string, string>
      }

      if (event.extra) {
        event.extra = scrubValue(event.extra) as Record<string, unknown>
      }

      if (event.contexts) {
        event.contexts = scrubValue(event.contexts) as Record<
          string,
          Record<string, unknown>
        >
      }

      return event
    },
  })

  markSentryInitialized(Sentry)

  Sentry.addBreadcrumb({
    category: "monitor",
    message: "monitor.ready",
    level: "info",
    data: {
      feature: "monitor",
      platform: getMonitorPlatform(),
      environment: config.environment ?? "unknown",
    },
  })
}
